import { useUser } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { PERMISSIONS } from '../config/permissions';

/**
 * Hook to check user permissions from Clerk publicMetadata.
 * Supports:
 *   - role: "admin"  → full access
 *   - permissions: ["group-data", "step-study", ...]  → custom access
 *   - stepStudyCohortIds: ["id1", "id2"]  → cohort-specific Step Study access
 */
export function usePermissions() {
  const { user } = useUser();

  return useMemo(() => {
    if (!user) {
      return {
        hasPermission: () => false,
        isAdmin: false,
        permissions: [],
        stepStudyCohortIds: [],
        canCreateStepStudyCohort: false,
        canAccessStepStudyCohort: () => false,
      };
    }

    const metadata = user.publicMetadata || {};
    const role = metadata.role;
    const permissions = Array.isArray(metadata.permissions) ? metadata.permissions : [];
    const stepStudyCohortIds = Array.isArray(metadata.stepStudyCohortIds)
      ? metadata.stepStudyCohortIds.filter((id) => id && typeof id === 'string')
      : [];

    const isAdmin = role === PERMISSIONS.ADMIN;
    const userPerms = new Set(permissions);
    const hasStepStudy = isAdmin || userPerms.has('step-study');

    const hasPermission = (permission) => {
      if (isAdmin) return true;
      if (!permission) return true; // No permission required
      return userPerms.has(permission);
    };

    const canCreateStepStudyCohort = hasStepStudy && (isAdmin || stepStudyCohortIds.length === 0);

    const canAccessStepStudyCohort = (cohortId) => {
      if (!hasStepStudy) return false;
      if (isAdmin) return true;
      if (stepStudyCohortIds.length === 0) return true;
      return stepStudyCohortIds.includes(String(cohortId));
    };

    return {
      hasPermission,
      isAdmin,
      permissions: Array.from(userPerms),
      stepStudyCohortIds,
      canCreateStepStudyCohort,
      canAccessStepStudyCohort,
    };
  }, [user]);
}
